import React, { useRef, Dispatch, SetStateAction, useEffect } from 'react';
import { Box, Button, Checkbox, CircularProgress, IconButton, Stack, Tooltip } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import { IQuestion } from './AIStudioQuestions';
import { classes } from './styles';
import ConfettiExplosion from 'react-confetti-explosion';
import QuestionSwitch from '../SharedComponents/Inputs/Switch';
import { classes as aiStudioClasses } from '../AIStudioModal/styles';
import { classes as sharedClasses } from '../SharedComponents/styles';
import { LoadingOverlay } from '../AIStudioModal/components/LoadingOverlay';
import { useHighlightOnNewItems } from '../../NewUI/hooks';
import { DynamicFooterWithInput } from '../SharedComponents/Footers/DynamicFoorterWithInput';

const SUGGESTED_PROMPTS = [
  'Ask about relevant experience',
  'Generate a situational judgement question',
  'Ask a question about soft skills'
];

export default function AIStudioResponse({
  generatedResponse,
  setGeneratedResponse,
  generateQuestions,
  setCurrentStep,
  handleSubmit,
  isCreating,
  isLoading,
  newQuestions,
  hasFeedbackInput
}: {
  generatedResponse: IQuestion[] | undefined;
  setGeneratedResponse: Dispatch<SetStateAction<IQuestion[] | undefined>>;
  generateQuestions: (feedbackInput?: string) => void;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  handleSubmit: () => void;
  isCreating: boolean;
  isLoading: boolean;
  newQuestions: IQuestion[];
  hasFeedbackInput: boolean;
}) {
  const playConfetti = useRef(true);
  const questionsContainerRef = useRef<HTMLDivElement>(null);
  const highlightedQuestions = useHighlightOnNewItems(newQuestions);

  const selectedCount = generatedResponse?.filter((item) => item.selected).length ?? 0;
  const addButtonText = `Add ${selectedCount > 0 ? `(${selectedCount})` : ''}`;
  const disableButton =
    (generatedResponse && generatedResponse.filter((item) => item.selected).length < 1) ||
    isLoading;

  const handleSwitchChange = (index: number, type: string) => {
    if (!generatedResponse) return;

    playConfetti.current && (playConfetti.current = false);
    const newQuestions = [...generatedResponse];
    switch (type) {
      case 'enabled':
        newQuestions[index].enabled = !newQuestions[index].enabled;
        break;
      case 'required':
        newQuestions[index].required = !newQuestions[index].required;
        break;
      case 'rateable':
        newQuestions[index].rateable = !newQuestions[index].rateable;
        break;
      case 'confidential':
        newQuestions[index].confidential = !newQuestions[index].confidential;
        break;
      default:
        break;
    }
    setGeneratedResponse(newQuestions);
  };

  const handleSelectQuestion = (index: number) => {
    if (!generatedResponse) return;

    playConfetti.current && (playConfetti.current = false);
    const newQuestions = [...generatedResponse];
    newQuestions[index].selected = !newQuestions[index].selected;
    setGeneratedResponse(newQuestions);
  };

  useEffect(() => {
    if (!isLoading && questionsContainerRef.current && hasFeedbackInput) {
      questionsContainerRef.current.scrollTo({
        top: questionsContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [hasFeedbackInput, isLoading]);

  setTimeout(() => {
    playConfetti.current = false;
  }, 3500);

  if (generatedResponse === undefined) {
    return (
      <Box sx={classes.aiQuestionsContainer}>
        There was an error generating your questions. Please try again.
      </Box>
    );
  }

  return (
    <>
      {playConfetti.current && (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <ConfettiExplosion
            force={0.5}
            duration={4000}
            particleCount={120}
            height={1000}
            width={1000}
          />
        </Box>
      )}
      <Box sx={classes.aiQuestionsContainer}>
        <Box sx={classes.descriptionHeader}>
          <Box sx={classes.descriptionHeaderText}>Your screening questions</Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              id="back-to-edit-fields-screening-question-integration"
              sx={classes.footerBackButton}
              onClick={() => setCurrentStep(0)}
            >
              Back
            </Button>

            <Tooltip placement="top" title={'Regenerate'} arrow>
              <IconButton
                id="regenerate-questions-integration-button"
                disabled={isLoading}
                onClick={() => generateQuestions('')}
                sx={aiStudioClasses.actionButton}
                size="small"
              >
                <CachedIcon />
              </IconButton>
            </Tooltip>
            <Button
              id="add-generated-questions-to-job-button"
              sx={classes.footerAddButton}
              onClick={handleSubmit}
              disabled={disableButton}
            >
              {isCreating ? <CircularProgress size={20} color="inherit" /> : addButtonText}
            </Button>
          </Stack>
        </Box>
        <Box sx={aiStudioClasses.questionsViewport}>
          <Box
            sx={{ ...classes.questionsContainer, position: 'static' }}
            ref={questionsContainerRef}
          >
            {generatedResponse.map((item, index) => {
              const isHighlighted = highlightedQuestions.includes(item);
              return (
                <Box
                  sx={{
                    ...sharedClasses.question,
                    justifyContent: 'flex-start',
                    transition: 'all 0.3s ease-in-out',
                    ...(isHighlighted && {
                      ...sharedClasses.questionActive
                    })
                  }}
                  key={index}
                >
                  <Box>
                    <Checkbox
                      sx={classes.checkbox}
                      checked={item.selected}
                      onChange={() => handleSelectQuestion(index)}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '16px' }}>
                    <Box sx={{ marginTop: '7px' }}>{item.title}</Box>
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          '& .MuiFormControlLabel-label': {
                            color: '#666666 !important'
                          }
                        }}
                      >
                        <QuestionSwitch
                          item={item}
                          index={index}
                          type="enabled"
                          label="Enabled"
                          onChange={handleSwitchChange}
                        />
                        <QuestionSwitch
                          item={item}
                          index={index}
                          type="required"
                          label="Required"
                          onChange={handleSwitchChange}
                        />
                        <QuestionSwitch
                          item={item}
                          index={index}
                          type="rateable"
                          label="Rateable"
                          onChange={handleSwitchChange}
                        />
                        <QuestionSwitch
                          item={item}
                          index={index}
                          type="confidential"
                          label="Confidential"
                          onChange={handleSwitchChange}
                        />
                      </Box>
                    </Box>
                  </Box>
                  {generatedResponse[index].confidential && (
                    <Box sx={{ padding: '0 6px', color: '#838383', fontSize: '12px' }}>
                      <span style={{ fontWeight: 'bold' }}>Confidential Setting Enabled:</span> For
                      DE&I compliance, only authorised users (HR Admins), can view responses. Hiring
                      Managers will not have access.
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          <LoadingOverlay isLoading={isLoading} generationType={'questions'} />
        </Box>
        <DynamicFooterWithInput
          isLoading={isLoading}
          submitCallback={generateQuestions}
          description={
            <>
              Ta-da! Your <span>screening questions</span> are ready for action, let&apos;s get that
              position filled!
            </>
          }
          suggestedPrompts={SUGGESTED_PROMPTS}
        />
      </Box>
    </>
  );
}
